import { useState } from 'react';
import { axisLabel, expensesOf, incomeOf, monthLong, niceAxis } from '@/lib/financialOverview';
import { formatCurrency } from '@/lib/formatters';
import { cn } from '@/lib/cn';
import type { OverviewMonth } from '@/types/table';

const PLOT_HEIGHT = 200;
const TIP_WIDTH = 'w-48';

interface Slot {
  month: string;
  label: string;
  data: OverviewMonth | null;
}

interface Props {
  slots: Slot[];
  selected: string | null;
  currency: string;
  onSelect: (month: string | null) => void;
}

/** Income and expenses per month on one axis. Clicking a month filters the page. */
export function OverviewMonthChart({ slots, selected, currency, onSelect }: Props) {
  const [hover, setHover] = useState<string | null>(null);
  const peak = Math.max(0, ...slots.flatMap((s) => (s.data ? [incomeOf(s.data), expensesOf(s.data)] : [])));
  const ticks = niceAxis(peak);
  const top = ticks[ticks.length - 1];
  const px = (value: number) => Math.round((Math.max(0, value) / top) * PLOT_HEIGHT);
  const money = (value: number) => formatCurrency(value, currency);

  return (
    // The padding keeps the top axis label inside the scroll box, which clips.
    <div className="overflow-x-auto pt-2">
      <div className="flex min-w-[620px] gap-2">
        <div aria-hidden="true" className="relative w-11 flex-none" style={{ height: PLOT_HEIGHT }}>
          {ticks.map((t) => (
            <span key={t} className="absolute right-0 translate-y-1/2 text-[11.5px] tabular-nums text-muted-foreground" style={{ bottom: px(t) }}>
              {axisLabel(t, currency)}
            </span>
          ))}
        </div>
        <div className="relative min-w-0 flex-1">
          <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0" style={{ height: PLOT_HEIGHT }}>
            {ticks.map((t) => <span key={t} className="absolute inset-x-0 h-px bg-border/60" style={{ bottom: px(t) }} />)}
          </div>
          <div className="relative flex gap-0.5">
            {slots.map((slot, i) => {
              const has = !!slot.data;
              const on = selected === slot.month;
              const income = slot.data ? incomeOf(slot.data) : 0;
              const expenses = slot.data ? expensesOf(slot.data) : 0;
              const dimmed = !!selected && !on;
              return (
                <button
                  key={slot.month}
                  type="button"
                  disabled={!has}
                  aria-pressed={on}
                  aria-label={has ? `${monthLong(slot.month)}: income ${money(income)}, expenses ${money(expenses)}` : `${monthLong(slot.month)}: no entries`}
                  onClick={() => onSelect(on ? null : slot.month)}
                  onMouseEnter={() => setHover(slot.month)}
                  onMouseLeave={() => setHover(null)}
                  onFocus={() => setHover(slot.month)}
                  onBlur={() => setHover(null)}
                  className={cn('relative flex min-w-0 flex-1 flex-col rounded-lg disabled:cursor-default', on && 'bg-primary/10', has && !on && 'hover:bg-muted/60')}
                >
                  <span className="flex items-end justify-center gap-0.5" style={{ height: PLOT_HEIGHT }}>
                    <span className={cn('w-4 rounded-t bg-blue-600 dark:bg-blue-500', dimmed && 'opacity-35')} style={{ height: px(income) }} />
                    <span className={cn('w-4 rounded-t bg-orange-600', dimmed && 'opacity-35')} style={{ height: px(expenses) }} />
                  </span>
                  <span className={cn('flex h-7 items-center justify-center text-xs', on ? 'font-semibold text-foreground' : has ? 'text-muted-foreground' : 'text-muted-foreground/50')}>
                    {slot.label}
                  </span>
                  {hover === slot.month && slot.data && (
                    // Sits inside the plot: the scroll box around the chart would clip anything above it.
                    <span className={cn(
                      'pointer-events-none absolute top-1 z-10 flex flex-col gap-0.5 rounded-lg bg-popover p-3 text-left text-xs text-popover-foreground shadow-lg ring-1 ring-border',
                      TIP_WIDTH,
                      i < 2 ? 'left-0' : i > slots.length - 3 ? 'right-0' : 'left-1/2 -translate-x-1/2',
                    )}>
                      <span className="font-semibold">{monthLong(slot.month)}</span>
                      <TipRow label="Income" value={money(income)} />
                      <TipRow label="Expenses" value={money(expenses)} />
                      <TipRow label="Month-end cash" value={money(slot.data.closingBalance)} />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function TipRow({ label, value }: { label: string; value: string }) {
  return (
    <span className="flex justify-between gap-2">
      <span className="text-muted-foreground">{label}</span>
      <span className="tabular-nums">{value}</span>
    </span>
  );
}
